import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import StoryPointsTooltip from './StoryPointsTooltip';
import { getStoryPointsForResource, getOriginalEstimateForResource } from './helpers/getStoryPointsData';
import ResourceRow from "./ResourceRow";
import useIntegrationStatus from "../../../../hooks/useIntegrationStatus";

export interface Group {
  id: string;
  title: string;
  rightTitle?: string; // comma-separated fallback
  skills?: string[];   // preferred array source
}

interface TimelineLeftPanelProps {
  groups: Group[];
  leftboxWidth: number;
  boxheight: number;
  capacityPlanningData?: any;
  searchType?: string;
  teamFilterState?: any;
  selectedTeams?: string[];
  onViewResourceTasks?: (resourceId: string) => void;
}

const TimelineLeftPanel: React.FC<TimelineLeftPanelProps> = ({
  groups,
  leftboxWidth,
  boxheight,
  capacityPlanningData = {},
  searchType = '',
  teamFilterState = {},
  selectedTeams = [],
  onViewResourceTasks,
}) => {
  const { isIntegrationActive } = useIntegrationStatus();
  const { capacityPlanningLoading } = useSelector((state: any) => state.resources);
  const showMetrics = isIntegrationActive('azure') && searchType === 'sprint';

  // Use same Flowbite-inspired color palette as PeopleCard for skill pills
  const getSkillColor = (index: number) => {
    const colors = [
      'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800',
    ];
    return colors[index % colors.length];
  };

  const [screenHeight, setScreenHeight] = useState(typeof window !== 'undefined' ? window.innerHeight : 600);
  useEffect(() => {
    const handleResize = () => setScreenHeight(window.innerHeight);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get story points data for a resource
  const getStoryPointsData = (resourceId: string) => {
    return getStoryPointsForResource(
      resourceId,
      capacityPlanningData,
      searchType,
      teamFilterState
    );
  };

  // Get original estimate data for a resource
  const getOriginalEstimateData = (resourceId: string) => {
    return getOriginalEstimateForResource(
      resourceId,
      capacityPlanningData,
      searchType,
      teamFilterState
    );
  };

  return (
    <div
      id="left-panel"
      className="sticky left-0 z-20 bg-white dark:bg-tertiary-dark border-r border-gray-200 dark:border-[#374151]"
      style={{
        height: '100%',
        width: `${leftboxWidth}px`
      }}
    >
      {groups && groups.length && groups.map((group: Group) => {
        // prefer skills array if present, else derive from rightTitle
        const skillsSource = group.skills && group.skills.length ? group.skills : (group.rightTitle ? group.rightTitle.split(',') : []);
        const skills = skillsSource.map(s => s.trim()).filter(Boolean);
        const storyPointsData = getStoryPointsData(group.id);
        const originalEstimateData = getOriginalEstimateData(group.id);
        const displayStoryPoints = storyPointsData?.total || 0;
        const displayOriginalEstimate = originalEstimateData?.total || 0;
        const displayTaskCount = storyPointsData?.totalTasks || 0;

        // Check if this resource is optional (not imported into Cadence)
        const isOptionalResource = Boolean((group as any).isOptionalResource === true);
        // Show "Add to team" button only when exactly 1 team is selected
        const showImportCTA = Boolean(isOptionalResource && searchType === 'sprint' && selectedTeams && selectedTeams.length === 1);

        return (
          <ResourceRow
            key={group.id}
            group={group}
            skills={skills}
            leftboxWidth={leftboxWidth}
            boxheight={boxheight}
            getSkillColor={getSkillColor}
            displayStoryPoints={displayStoryPoints}
            displayOriginalEstimate={displayOriginalEstimate}
            displayTaskCount={displayTaskCount}
            showMetrics={showMetrics}
            isOptionalResource={isOptionalResource}
            capacityPlanningLoading={capacityPlanningLoading}
            searchType={searchType}
            selectedTeams={selectedTeams}
            tooltipContent={
              <StoryPointsTooltip
                resourceName={group.title}
                storyPointsData={storyPointsData}
                originalEstimateData={originalEstimateData}
                skills={skills}
                isOptionalResource={isOptionalResource}
                showImportCTA={showImportCTA}
                resourceId={group.id}
                selectedTeams={selectedTeams}
                onViewTasks={onViewResourceTasks ? () => onViewResourceTasks(group.id) : undefined}
              />
            }
          />
        );
      })}

      {/* Add empty resource rows to left panel */}
      {(() => {
        const currentResourcesHeight = groups.length * boxheight;
        const availableHeight = screenHeight - 140;
        const emptyRowsNeeded = Math.max(0, Math.ceil((availableHeight - currentResourcesHeight) / boxheight));

        return Array.from({ length: emptyRowsNeeded }).map((_, emptyIndex) => (
          <div
            key={`empty-left-panel-${emptyIndex}`}
            style={{
              width: leftboxWidth + 1,
              height: boxheight,
              boxShadow: '4px 0 8px rgba(0,0,0,0.1)',
            }}
            className="relative border-r-2 shadow-xl  dark:border-[#374151] border-b border-gray-300 dark:border-gray-600/50 transition-colors duration-150 bg-light-surfacePrimary dark:bg-secondary-dark hover:bg-hover-light dark:hover:bg-hover-dark"
          >
            <div
              className="cursor-pointer items-center h-full p-3"
              style={{ width: leftboxWidth, height: `${boxheight - 1}px` }}
            >
              {/* Empty resource row - no content */}
            </div>
          </div>
        ));
      })()}
    </div>
  );
};

export default TimelineLeftPanel;